import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Select, theme } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import { Table } from '@surecompanies/core_components'
import React, { useMemo, useState } from 'react'

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const

export interface DataTableProps<T> {
  readonly columns: ColumnsType<T>
  readonly dataSource: readonly T[]
  readonly rowKey: string | ((record: T) => React.Key)
  readonly loading?: boolean
  readonly emptyText?: string
  readonly pageSizeOptions?: readonly number[]
  readonly defaultPageSize?: number
  readonly hidePagination?: boolean
  readonly onRow?: TableProps<T>['onRow']
  readonly tableLayout?: TableProps<T>['tableLayout']
}

/**
 * Shared paginated table: wraps the design system's antd `Table` for row
 * rendering and adds a "Page Size / range / page nav" footer (Figma ABP
 * table spec), since neither antd's default pagination nor the codebase's
 * other table wrappers (ag-grid based) render that exact footer shape.
 */
export function DataTable<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading = false,
  emptyText = 'No data',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  defaultPageSize = pageSizeOptions[0] ?? 10,
  hidePagination = false,
  onRow,
  tableLayout,
}: DataTableProps<T>) {
  const { token } = theme.useToken()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  const total = dataSource.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const currentPage = Math.min(page, totalPages)
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const rangeEnd = Math.min(currentPage * pageSize, total)
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPages

  const pageRows = useMemo(
    () =>
      hidePagination
        ? dataSource
        : dataSource.slice(
            (currentPage - 1) * pageSize,
            currentPage * pageSize
          ),
    [dataSource, currentPage, pageSize, hidePagination]
  )

  function navIconStyle(disabled: boolean): React.CSSProperties {
    return {
      color: disabled ? token.colorTextDisabled : token.colorTextSecondary,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 12,
    }
  }

  function onPageSizeChange(nextPageSize: number) {
    setPageSize(nextPageSize)
    setPage(1)
  }

  return (
    <div>
      <Table<T>
        columns={columns}
        dataSource={pageRows as T[]}
        rowKey={rowKey}
        loading={loading}
        pagination={false}
        locale={{ emptyText }}
        onRow={onRow}
        tableLayout={tableLayout}
      />
      {!hidePagination && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 24,
            padding: '12px 4px 0',
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Page Size:</span>
            <Select
              size="small"
              value={pageSize}
              style={{ width: 68 }}
              onChange={onPageSizeChange}
              options={pageSizeOptions.map((size) => ({
                value: size,
                label: size,
              }))}
            />
          </div>
          <span>
            {rangeStart} to {rangeEnd} of {total}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <DoubleLeftOutlined
              style={navIconStyle(isFirstPage)}
              onClick={isFirstPage ? undefined : () => setPage(1)}
            />
            <LeftOutlined
              style={navIconStyle(isFirstPage)}
              onClick={isFirstPage ? undefined : () => setPage(currentPage - 1)}
            />
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <RightOutlined
              style={navIconStyle(isLastPage)}
              onClick={isLastPage ? undefined : () => setPage(currentPage + 1)}
            />
            <DoubleRightOutlined
              style={navIconStyle(isLastPage)}
              onClick={isLastPage ? undefined : () => setPage(totalPages)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
